/**
 * Hand-rolled icon set. No icon package, so every glyph here is a 24x24 stroke
 * path drawn on the same grid with the same 1.75 stroke weight.
 *
 * Icons are decorative by default. When an icon is the only content of a
 * control, the control needs its own accessible name.
 */
import type { SVGProps } from 'react'
import { cn } from '../lib/cn'

export type IconProps = Omit<SVGProps<SVGSVGElement>, 'children'>

function Icon({ className, ...props }: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cn('h-[1.15em] w-[1.15em] shrink-0', className)}
      {...props}
    />
  )
}

export const IconDashboard = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3.5 12.5 12 5l8.5 7.5" />
    <path d="M5.5 11v7.5a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V11" />
  </Icon>
)

export const IconApprovals = (p: IconProps) => (
  <Icon {...p}>
    <path d="M9 4.5h6l1.5 2H19a1 1 0 0 1 1 1V19a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7.5a1 1 0 0 1 1-1h2.5Z" />
    <path d="m9 13 2 2 4-4" />
  </Icon>
)

export const IconQueue = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3.5" y="4.5" width="17" height="6" rx="1.5" />
    <rect x="3.5" y="13.5" width="17" height="6" rx="1.5" />
    <path d="M7 7.5h.01M7 16.5h.01" />
  </Icon>
)

export const IconInbox = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 13.5 6.2 5.8A1.5 1.5 0 0 1 7.6 4.7h8.8a1.5 1.5 0 0 1 1.4 1.1L20 13.5" />
    <path d="M4 13.5h4l1 2.5h6l1-2.5h4v4.3a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.8Z" />
  </Icon>
)

export const IconProspects = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="9.5" cy="8.5" r="3" />
    <path d="M3.8 19.2a6 6 0 0 1 11.4 0" />
    <path d="M16.5 6.2a3 3 0 0 1 0 5.6M17.8 19.2a6 6 0 0 0-1.6-3.6" />
  </Icon>
)

export const IconCampaigns = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 10.5v3a1.5 1.5 0 0 0 1.5 1.5H8l6 4V6.5l-6 4H5.5A1.5 1.5 0 0 0 4 12Z" />
    <path d="M17.5 9a4 4 0 0 1 0 6" />
  </Icon>
)

export const IconMethods = (p: IconProps) => (
  <Icon {...p}>
    <rect x="4" y="3.5" width="7" height="5" rx="1.2" />
    <rect x="13" y="15.5" width="7" height="5" rx="1.2" />
    <path d="M7.5 8.5V16a2 2 0 0 0 2 2H13" />
    <path d="M16.5 15.5V12a2 2 0 0 0-2-2h-3.5" />
  </Icon>
)

export const IconAnalytics = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 19.5h16" />
    <path d="M7 19.5v-6M12 19.5V6.5M17 19.5v-9" />
  </Icon>
)

export const IconSettings = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="2.75" />
    <path d="M12 3.5v2M12 18.5v2M20.5 12h-2M5.5 12h-2M18 6l-1.4 1.4M7.4 16.6 6 18M18 18l-1.4-1.4M7.4 7.4 6 6" />
  </Icon>
)

export const IconAccounts = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3.5 19.5 6v6c0 4-3.2 7.2-7.5 8.5C7.7 19.2 4.5 16 4.5 12V6Z" />
    <path d="m9.2 12.2 2 2 3.6-3.8" />
  </Icon>
)

export const IconBrands = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4.5 10.5 12 3.8l7.5 6.7v8.2a1.5 1.5 0 0 1-1.5 1.5H6a1.5 1.5 0 0 1-1.5-1.5Z" />
    <path d="M9.75 20.2v-5.4h4.5v5.4" />
  </Icon>
)

export const IconKnowledge = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5 5.2A1.2 1.2 0 0 1 6.2 4H12v16H6.2A1.2 1.2 0 0 1 5 18.8Z" />
    <path d="M12 4h5.8A1.2 1.2 0 0 1 19 5.2v13.6a1.2 1.2 0 0 1-1.2 1.2H12" />
  </Icon>
)

export const IconShield = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3.5 19.5 6v6c0 4-3.2 7.2-7.5 8.5C7.7 19.2 4.5 16 4.5 12V6Z" />
    <path d="M9 12h6" />
  </Icon>
)

export const IconMailbox = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3.5" y="5.5" width="17" height="13" rx="1.5" />
    <path d="m4 7 8 5.5L20 7" />
  </Icon>
)

export const IconPolicy = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 3.8h12a1 1 0 0 1 1 1v14.4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4.8a1 1 0 0 1 1-1Z" />
    <path d="M8.5 8.5h7M8.5 12h7M8.5 15.5h4" />
  </Icon>
)

export const IconChevronDown = (p: IconProps) => (
  <Icon {...p}>
    <path d="m6.5 9.5 5.5 5 5.5-5" />
  </Icon>
)

export const IconChevronRight = (p: IconProps) => (
  <Icon {...p}>
    <path d="m9.5 6 5.5 6-5.5 6" />
  </Icon>
)

export const IconChevronLeft = (p: IconProps) => (
  <Icon {...p}>
    <path d="M14.5 6 9 12l5.5 6" />
  </Icon>
)

export const IconCheck = (p: IconProps) => (
  <Icon {...p}>
    <path d="m5 12.5 4.5 4.5L19 7" />
  </Icon>
)

export const IconClose = (p: IconProps) => (
  <Icon {...p}>
    <path d="m6.5 6.5 11 11M17.5 6.5l-11 11" />
  </Icon>
)

export const IconSearch = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="11" cy="11" r="6" />
    <path d="m15.5 15.5 4 4" />
  </Icon>
)

export const IconAlert = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 4.8 21 19.2H3Z" />
    <path d="M12 10v4M12 16.6h.01" />
  </Icon>
)

export const IconInfo = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 11v5.5M12 7.8h.01" />
  </Icon>
)

export const IconBlocked = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="m6.2 6.2 11.6 11.6" />
  </Icon>
)

export const IconOffline = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 8.5a13 13 0 0 1 16 0M7 12a8.5 8.5 0 0 1 10 0M10 15.4a4 4 0 0 1 4 0M12 19h.01" />
    <path d="m4.5 4.5 15 15" />
  </Icon>
)

export const IconCopy = (p: IconProps) => (
  <Icon {...p}>
    <rect x="9" y="9" width="11" height="11" rx="1.5" />
    <path d="M15 6.5V5.5A1.5 1.5 0 0 0 13.5 4h-8A1.5 1.5 0 0 0 4 5.5v8A1.5 1.5 0 0 0 5.5 15h1" />
  </Icon>
)

export const IconExternal = (p: IconProps) => (
  <Icon {...p}>
    <path d="M14 5h5v5" />
    <path d="M19 5l-7.5 7.5" />
    <path d="M18 14v4.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 4 18.5v-11A1.5 1.5 0 0 1 5.5 6H10" />
  </Icon>
)

export const IconMenu = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </Icon>
)

export const IconLogout = (p: IconProps) => (
  <Icon {...p}>
    <path d="M14.5 5.5h3A1.5 1.5 0 0 1 19 7v10a1.5 1.5 0 0 1-1.5 1.5h-3" />
    <path d="M11 15.5 14.5 12 11 8.5M14 12H4.5" />
  </Icon>
)

export const IconPlus = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 5.5v13M5.5 12h13" />
  </Icon>
)

export const IconMinus = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5.5 12h13" />
  </Icon>
)

/** Fit the whole drawing in view. A frame with a dot in the middle, so it does not read as full screen. */
export const IconFitView = (p: IconProps) => (
  <Icon {...p}>
    <rect x="4.5" y="6" width="15" height="12" rx="1.5" />
    <circle cx="12" cy="12" r="1.6" />
  </Icon>
)

export const IconFilter = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4.5 6h15l-5.8 6.6v5.2l-3.4 1.7v-6.9Z" />
  </Icon>
)

export const IconClock = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </Icon>
)

export const IconLock = (p: IconProps) => (
  <Icon {...p}>
    <rect x="5" y="10.5" width="14" height="9" rx="1.5" />
    <path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" />
  </Icon>
)

export const IconKey = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="8.5" cy="12" r="3.5" />
    <path d="M12 12h8M17.5 12v3M20 12v2" />
  </Icon>
)

export const IconEye = (p: IconProps) => (
  <Icon {...p}>
    <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
    <circle cx="12" cy="12" r="2.75" />
  </Icon>
)

export const IconEyeOff = (p: IconProps) => (
  <Icon {...p}>
    {/* The open lids are cut back on both sides so the slash reads as covering
        the eye rather than crossing a shape that is still fully drawn. */}
    <path d="M4.5 8.75C3.2 10.1 2.5 12 2.5 12s3.5 6 9.5 6c1.5 0 2.8-.37 3.94-.93" />
    <path d="M19.1 15.4C20.7 14 21.5 12 21.5 12s-3.5-6-9.5-6c-.9 0-1.74.13-2.5.37" />
    <path d="M10.06 10.06a2.75 2.75 0 0 0 3.88 3.88" />
    <path d="m4 4 16 16" />
  </Icon>
)

export const IconRefresh = (p: IconProps) => (
  <Icon {...p}>
    <path d="M19 11a7 7 0 1 0-.6 4" />
    <path d="M19.5 5.5V11H14" />
  </Icon>
)

export const IconSparkle = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 4.5 13.6 9.4 18.5 11 13.6 12.6 12 17.5 10.4 12.6 5.5 11l4.9-1.6Z" />
    <path d="M18 16.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7Z" />
  </Icon>
)

export const IconUser = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="8.5" r="3.5" />
    <path d="M5 19.5a7 7 0 0 1 14 0" />
  </Icon>
)

export const IconSwap = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4.5 8.5h13L14 5" />
    <path d="M19.5 15.5h-13L10 19" />
  </Icon>
)

export const IconArrowRight = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4.5 12h15m0 0-5-5m5 5-5 5" />
  </Icon>
)

export const IconArrowDown = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 4.5v15m0 0 5-5m-5 5-5-5" />
  </Icon>
)

export const IconChevronUp = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 14.5 12 8.5l6 6" />
  </Icon>
)

export const IconTrash = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4.5 7h15M10 7V4.5h4V7M6.5 7l.8 12.5h9.4L17.5 7" />
  </Icon>
)

export const IconDownload = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 4.5v12m0 0-4-4m4 4 4-4" />
    <path d="M4.5 17v2.5h15V17" />
  </Icon>
)

/** Take the last change back. A curved arrow pointing left. */
export const IconUndo = (p: IconProps) => (
  <Icon {...p}>
    <path d="M9 7.5 5 11.5l4 4" />
    <path d="M5.5 11.5h9a4.5 4.5 0 0 1 0 9H12" />
  </Icon>
)

/** Put an undone change back. The undo arrow, mirrored. */
export const IconRedo = (p: IconProps) => (
  <Icon {...p}>
    <path d="m15 7.5 4 4-4 4" />
    <path d="M18.5 11.5h-9a4.5 4.5 0 0 0 0 9H12" />
  </Icon>
)

/** Make something fill the screen. Four corners pointing out. */
export const IconExpand = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4.5 9V4.5H9M15 4.5h4.5V9M19.5 15v4.5H15M9 19.5H4.5V15" />
  </Icon>
)

/** Put it back in the page. Four corners pointing in. */
export const IconCollapse = (p: IconProps) => (
  <Icon {...p}>
    <path d="M9 4.5V9H4.5M19.5 9H15V4.5M15 19.5V15h4.5M4.5 15H9v4.5" />
  </Icon>
)

export const IconUpload = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 16.5V4.5m0 0-4 4m4-4 4 4" />
    <path d="M4.5 17v2.5h15V17" />
  </Icon>
)

/** A branch in a method: the flow continues one way or the other. */
export const IconBranch = (p: IconProps) => (
  <Icon {...p}>
    <path d="M7.5 4v6a4 4 0 0 0 4 4h5" />
    <path d="M16.5 10.5 20 14l-3.5 3.5" />
    <path d="M7.5 20v-3" />
  </Icon>
)

/** More than one person, as opposed to `IconUser`. */
export const IconPeople = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="9.5" cy="9" r="3.2" />
    <path d="M3.5 19a6 6 0 0 1 12 0" />
    <path d="M16 6.2a3.2 3.2 0 0 1 0 5.6M17.5 14.4c1.9.7 3 2.4 3 4.6" />
  </Icon>
)

/**
 * The channel marks. Brand logos redrawn on the same grid at the same weight as
 * everything else, so a column of them lines up and takes its colour from the
 * text around it. Each one is drawn as large as the grid allows, which is what
 * keeps it readable down at 14px. Email has no mark of its own: it uses
 * `IconMailbox`, which is already this envelope.
 */

export const IconLinkedIn = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5 9.5v10M5 5.6h.01" />
    <path d="M11 19.5v-10" />
    <path d="M11 13.8a4 4 0 0 1 8 0v5.7" />
  </Icon>
)

export const IconInstagram = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3.8" y="3.8" width="16.4" height="16.4" rx="4.6" />
    <circle cx="12" cy="12" r="3.7" />
    <path d="M16.4 7.6h.01" />
  </Icon>
)

/** Taller and so proportionally narrower than `IconClose`, which is a dismiss. */
export const IconX = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6.5 4.8 17.5 19.2M17.5 4.8 6.5 19.2" />
  </Icon>
)

export const IconFacebook = (p: IconProps) => (
  <Icon {...p}>
    <path d="M15.5 4.5h-1.6A3.4 3.4 0 0 0 10.5 7.9V19.5" />
    <path d="M7.6 10.8h6.8" />
  </Icon>
)

/** The P keeps its ring: on its own the letter reads as a letter, not a mark. */
export const IconPinterest = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M10.4 17.6V6.8" />
    <path d="M10.4 6.8h2.4a2.9 2.9 0 0 1 0 5.8h-2.4" />
  </Icon>
)

/**
 * The quaver, which is the half of TikTok's mark that survives at this size.
 *
 * The full logo is a note with a doubled offset outline in two colours, and both
 * of those are what make it recognisable on a billboard and illegible at 14px in
 * one stroke colour. The note alone keeps the silhouette.
 */
export const IconTikTok = (p: IconProps) => (
  <Icon {...p}>
    <path d="M9.2 13.2a3.1 3.1 0 1 0 3.1 3.1V4.8" />
    <path d="M12.3 4.8a4.3 4.3 0 0 0 4.5 4.2" />
  </Icon>
)

/**
 * The rounded screen with a play triangle, which is the whole of YouTube's mark.
 *
 * Wider than it is tall on purpose: the 16 by 9 proportion is doing as much of
 * the recognising as the triangle, and squaring it off turns it into a generic
 * play button that could belong to anything.
 */
export const IconYouTube = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="6.5" width="18" height="11" rx="3" />
    <path d="M10.6 9.9l4.6 2.6-4.6 2.6z" />
  </Icon>
)

/** Threads' open at-sign, the same three strokes the Publish cards draw. */
export const IconThreads = (p: IconProps) => (
  <Icon {...p}>
    <path d="M16.2 15.5a4.9 4.9 0 1 1 .1-6.7" />
    <path d="M15.6 12.9c0 1.7-1.3 2.9-3.1 2.9-1.6 0-2.7-.8-2.7-1.9s1.1-1.8 2.9-1.8c1.3 0 2.4.3 3.1.8" />
    <path d="M9.9 10.8c.5-1 1.5-1.6 2.7-1.6 1.6 0 2.7.9 3 2.4" />
  </Icon>
)

/** Mastodon's rounded bubble with the m inside it, which is the mark at this size. */
export const IconMastodon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5 9.5C5 5.5 7.6 4.5 12 4.5s7 1 7 5v3.2c0 3.3-2.4 4.4-6.6 4.6-1.6.1-3-.1-4-.4.2 1.8 1.6 2.6 4.6 2.4" />
    <path d="M9 13.2V9.8c0-1.6 2.6-1.6 2.6 0v2M11.6 11.8V9.8c0-1.6 2.6-1.6 2.6 0v3.4" />
  </Icon>
)

/** Reddit's alien head with its antenna, which is the mark at this size. */
export const IconReddit = (p: IconProps) => (
  <Icon {...p}>
    <ellipse cx="12" cy="14.2" rx="7" ry="4.8" />
    <path d="M12 9.4l1.3-4.6 3.6.9" />
    <circle cx="18.1" cy="6" r="1.2" />
    <path d="M9.4 15.9c1.5 1 3.7 1 5.2 0" />
    <circle cx="9.3" cy="13.2" r=".5" />
    <circle cx="14.7" cy="13.2" r=".5" />
  </Icon>
)

/** Bluesky's butterfly, both wings, which is the whole of the mark. */
export const IconBluesky = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 11.2C10.6 8.2 7.4 5.2 5 4.7c-1.4-.3-1.6 1.4-1.3 3 .4 2 1.2 3.6 4 3.9-2.9.6-3.3 2.7-1.4 4.2 2 1.6 3.7-.6 5.7-4.6" />
    <path d="M12 11.2c1.4-3 4.6-6 7-6.5 1.4-.3 1.6 1.4 1.3 3-.4 2-1.2 3.6-4 3.9 2.9.6 3.3 2.7 1.4 4.2-2 1.6-3.7-.6-5.7-4.6" />
  </Icon>
)

/**
 * The speech bubble with the tail at the lower left is what makes WhatsApp's
 * mark recognisable at this size. The handset inside it is dropped: at 14px it
 * reads as noise, and the bubble alone is the silhouette people know.
 */
/**
 * The paper plane, which is the whole of Telegram's mark at this size. Drawn as
 * one filled path rather than plane-inside-circle: the disc is what disappears
 * first on a badge, and the plane alone is the silhouette people know.
 */
/** Light theme. A sun, drawn as the set draws everything else: stroked, in em. */
export const IconSun = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </Icon>
)

/** Dark theme. */
export const IconMoon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
  </Icon>
)

/** Follow the machine. A display, because that is what the setting is about. */
export const IconDisplay = (p: IconProps) => (
  <Icon {...p}>
    <rect x="2.5" y="4" width="19" height="12" rx="2" />
    <path d="M8.5 20h7M12 16v4" />
  </Icon>
)

/**
 * The one filled glyph in the set, and the one that drew its own `<svg>`.
 *
 * Bypassing `Icon` meant bypassing its sizing, `h-[1.15em] w-[1.15em] shrink-0`,
 * so this mark had no intrinsic size and grew to fill whatever box it was put
 * in. Anywhere a caller passed an explicit height it looked fine, which is why
 * it survived: it only shows itself somewhere that sizes marks by the type scale,
 * and there it came out at ninety pixels beside its fourteen-pixel siblings.
 *
 * Still its own element rather than an `Icon`, because that wrapper is stroked
 * and this path is filled. What it no longer does is forget the size.
 */
export const IconTelegram = ({ className, ...p }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
    className={cn('h-[1.15em] w-[1.15em] shrink-0', className)}
    {...p}
  >
    <path d="M21.9 4.3 18.7 19c-.24 1.06-.87 1.32-1.76.82l-4.86-3.58-2.35 2.26c-.26.26-.48.48-.98.48l.35-4.94 9-8.13c.39-.35-.09-.54-.6-.2L7.4 12.02l-4.8-1.5c-1.04-.33-1.06-1.04.22-1.54l18.77-7.24c.87-.32 1.63.2 1.31 2.56z" />
  </svg>
)

export const IconWhatsApp = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3.9a8.1 8.1 0 1 1-4.2 15l-3.6 1 1-3.5A8.1 8.1 0 0 1 12 3.9Z" />
  </Icon>
)

/** A label somebody wrote, as opposed to a word the classifier chose. */
export const IconTag = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 4.8h6.4L20 14.4l-5.6 5.6L4.8 10.4Z" />
    <path d="M7.8 7.8h.01" />
  </Icon>
)

/** Filed away rather than deleted. The lid of a box, and the box under it. */
export const IconArchive = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3.5" y="4.5" width="17" height="4" rx="1" />
    <path d="M5 8.5v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-10" />
    <path d="M10 12.5h4" />
  </Icon>
)

/** A video camera: a recording, picked from Tella's library on a post. */
export const IconVideo = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="6" width="13" height="12" rx="2" />
    <path d="m16 10.5 5-3v9l-5-3" />
  </Icon>
)

// sf-shell
/** Up and down chevrons stacked: a control that picks one of several, such as the view switcher. */
export const IconSelector = (p: IconProps) => (
  <Icon {...p}>
    <path d="m8 9 4-4 4 4" />
    <path d="m8 15 4 4 4-4" />
  </Icon>
)

/** Three dots in a column: more for this thing, in a menu. */
export const IconDotsVertical = (p: IconProps) => (
  <Icon {...p}>
    <path strokeWidth={2.5} d="M12 5.5h.01M12 12h.01M12 18.5h.01" />
  </Icon>
)

// sf-tables
// The table shell's glyphs: the columns button, the row kebab, the sort
// arrows, the filter lines, the date button and the pencil a tag is edited by.
// Same grid and stroke as everything above.

/** Three panes side by side, for the button that picks which columns show. */
export const IconColumns = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3.5" y="4.75" width="17" height="14.5" rx="2" />
    <path d="M9.5 4.75v14.5M14.5 4.75v14.5" />
  </Icon>
)

export const IconArrowUp = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 19.5v-15m0 0-5 5m5-5 5 5" />
  </Icon>
)

/** An up arrow beside a down arrow, for a sort control that has no order yet. */
export const IconSwitchVertical = (p: IconProps) => (
  <Icon {...p}>
    <path d="M8 18.5v-13m0 0-3.5 3.5M8 5.5l3.5 3.5M16 5.5v13m0 0-3.5-3.5m3.5 3.5 3.5-3.5" />
  </Icon>
)

/** Three lines getting shorter, the filter mark the design draws. */
export const IconFilterLines = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5 7h14M7.5 12h9M10 17h4" />
  </Icon>
)

export const IconCalendar = (p: IconProps) => (
  <Icon {...p}>
    <rect x="4" y="5.5" width="16" height="14.5" rx="2" />
    <path d="M4 10h16M8.5 3.5v4M15.5 3.5v4" />
  </Icon>
)

export const IconEdit = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4.5 19.5h4l10.2-10.2a2 2 0 0 0 0-2.8l-1.2-1.2a2 2 0 0 0-2.8 0L4.5 15.5Z" />
    <path d="m13.5 6.5 4 4" />
  </Icon>
)

// sf-primitives
// Glyphs the redesigned primitives need. Appended as one block so the other
// work on this file merges cleanly around it.

/** Launch. The line rocket that replaces an emoji in a button label. */
export const IconRocket = (p: IconProps) => (
  <Icon {...p}>
    <path d="M14.5 4.5c2.5-1 4.5-1 5-.5s.5 2.5-.5 5l-5.5 5.5-4.5-4.5Z" />
    <path d="m9 10-3.5.5L3.5 13l4 1M14 15l-.5 3.5L11 20.5l-1-4" />
    <path d="M6.5 17.5c-1 .5-2 2-2 2s1.5-1 2-2c.5-.5 1-1 .5-1.5s-1 0-.5.5Z" />
    <path d="M15.5 8.5h.01" />
  </Icon>
)

/** The help marker: a question mark in a circle. */
export const IconHelpCircle = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M9.6 9.4a2.5 2.5 0 0 1 4.8.9c0 1.7-2.4 2.3-2.4 2.3M12 16.2h.01" />
  </Icon>
)

/** Something needs attention: an exclamation mark in a circle. */
export const IconAlertCircle = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.8v4.7M12 16.2h.01" />
  </Icon>
)

/** What a choice spends. A coin seen at a slight angle. */
export const IconCoin = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5v9M14.5 9.5c-.5-.7-1.4-1-2.5-1-1.4 0-2.5.7-2.5 1.7 0 2.3 5 1.2 5 3.6 0 1-1.1 1.7-2.5 1.7-1.1 0-2-.3-2.5-1" />
  </Icon>
)

// sf-overlays
// The glyphs the overlays and the setup cards draw. Each one is on the same
// 24 grid and the same stroke as the rest of the set.

/** A finished thing, inside a circle. The success toast leads with it. */
export const IconCheckCircle = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="m8.5 12.2 2.4 2.4 4.6-4.8" />
  </Icon>
)

/** A file going up, for the zone a file is dropped on. */
export const IconUploadCloud = (p: IconProps) => (
  <Icon {...p}>
    <path d="M8 17.5H6.8a3.8 3.8 0 0 1-.6-7.6 5.5 5.5 0 0 1 10.6-1.3 4.5 4.5 0 0 1 .7 8.9H16" />
    <path d="M12 20v-7.5m0 0-3 3m3-3 3 3" />
  </Icon>
)

/** One document, for the chip that names a file somebody picked. */
export const IconFile = (p: IconProps) => (
  <Icon {...p}>
    <path d="M13.5 3.5H7a1.5 1.5 0 0 0-1.5 1.5v14A1.5 1.5 0 0 0 7 20.5h10a1.5 1.5 0 0 0 1.5-1.5V8.5Z" />
    <path d="M13.5 3.5v5h5" />
  </Icon>
)

// inbox
// The thread toolbar, the composer and the label rail. They were drawn in the
// inbox's own folder while this file belonged to another track, on the same
// grid and stroke, and moved here whole. The inbox's archive glyph was the same
// lidded box as `IconArchive` above, so it uses that one.

/** A closed envelope, for the control that puts the unread mark back. */
export const IconEnvelope = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3.5" y="5.5" width="17" height="13" rx="1.5" />
    <path d="m4 7 8 5.5L20 7" />
  </Icon>
)

/** An open envelope, for the control that takes it off. */
export const IconEnvelopeOpen = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3.5 10.5 12 4.5l8.5 6v7a1.5 1.5 0 0 1-1.5 1.5H5a1.5 1.5 0 0 1-1.5-1.5Z" />
    <path d="m3.5 10.5 8.5 6 8.5-6" />
  </Icon>
)

/** A folder with an arrow going into it, for choosing where a thread goes. */
export const IconMove = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3.5 7.5a1 1 0 0 1 1-1h4l1.5 2H19.5a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1Z" />
    <path d="M12 11v5" />
    <path d="m9.5 13.5 2.5 2.5 2.5-2.5" />
  </Icon>
)

/** An arrow turning back on itself, for replying. */
export const IconReply = (p: IconProps) => (
  <Icon {...p}>
    <path d="M9 6.5 4 11l5 4.5" />
    <path d="M4 11h9a6 6 0 0 1 6 6v1" />
  </Icon>
)

/** A bell, for the date that puts a thread on the Reminders tab. */
export const IconBell = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6.5 10a5.5 5.5 0 0 1 11 0c0 3.5.8 5 1.5 6H5c.7-1 1.5-2.5 1.5-6Z" />
    <path d="M10 19a2 2 0 0 0 4 0" />
  </Icon>
)

/** Three dots, for the two dispositions the design has no button for. */
export const IconMore = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 12h.01M12 12h.01M18 12h.01" />
  </Icon>
)

/*
  The label rail's glyphs. The reference draws a line icon per label (a thumb for Positive, a crossed person
  for Wrong contact, a ticked calendar for Meeting completed) and this product's
  classifier has a few more outcomes than the reference has labels, so each of
  ours gets the nearest honest glyph rather than a dot.
*/

/** A raised thumb, for a positive reply. */
export const IconThumbsUp = (p: IconProps) => (
  <Icon {...p}>
    <path d="M7.5 10.5v9h-3v-9Z" />
    <path d="M7.5 10.5 11 4a2 2 0 0 1 2.5 2l-.6 3.5h5a1.8 1.8 0 0 1 1.8 2.1l-1.2 6.2a2 2 0 0 1-2 1.7H7.5" />
  </Icon>
)

/** A lowered thumb, for a negative one. */
export const IconThumbsDown = (p: IconProps) => (
  <Icon {...p}>
    <path d="M16.5 13.5v-9h3v9Z" />
    <path d="M16.5 13.5 13 20a2 2 0 0 1-2.5-2l.6-3.5h-5a1.8 1.8 0 0 1-1.8-2.1l1.2-6.2a2 2 0 0 1 2-1.7h9" />
  </Icon>
)

/** A speech bubble with a question mark, for a question back. */
export const IconQuestion = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4.5 18.5 6 15a7.5 7.5 0 1 1 3 3Z" />
    <path d="M10.3 9.6a2 2 0 0 1 3.8.9c0 1.3-2 1.6-2 2.9M12 15.5h.01" />
  </Icon>
)

/** A level line in a circle, for a reply that says neither yes nor no. */
export const IconNeutral = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M8.5 12h7" />
  </Icon>
)

/** A person with a cross beside them, for the wrong contact. */
export const IconUserX = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="9.5" cy="8" r="3.5" />
    <path d="M3.5 19.5a6 6 0 0 1 12 0" />
    <path d="m17 9 4 4M21 9l-4 4" />
  </Icon>
)

/** A struck circle, for a hostile reply. */
export const IconHostile = (p: IconProps) => (
  <Icon {...p}>
    <path d="M8.5 3.5h7l5 5v7l-5 5h-7l-5-5v-7Z" />
    <path d="M12 8v4.5M12 16h.01" />
  </Icon>
)

/** An envelope with a line through it, for an unsubscribe. */
export const IconUnsubscribe = (p: IconProps) => (
  <Icon {...p}>
    <path d="M20.5 12V7a1.5 1.5 0 0 0-1.5-1.5H5A1.5 1.5 0 0 0 3.5 7v10A1.5 1.5 0 0 0 5 18.5h7" />
    <path d="m4 7 8 5.5L20 7" />
    <path d="M15.5 17.5h5" />
  </Icon>
)

/** A sun low over a line, for an out of office reply. */
export const IconAway = (p: IconProps) => (
  <Icon {...p}>
    <path d="M7 16a5 5 0 0 1 10 0" />
    <path d="M3.5 19.5h17M12 5.5v2M5.6 9.6l1.4 1.4M18.4 9.6 17 11" />
  </Icon>
)

/** A calendar with a tick on it, for a meeting that happened. */
export const IconCalendarCheck = (p: IconProps) => (
  <Icon {...p}>
    <rect x="4" y="5.5" width="16" height="14.5" rx="2" />
    <path d="M4 10h16M8.5 3.5v4M15.5 3.5v4" />
    <path d="m9.5 14.5 2 2 3.5-3.5" />
  </Icon>
)

/** Two reply arrows, for answering everybody a message was copied to. */
export const IconReplyAll = (p: IconProps) => (
  <Icon {...p}>
    <path d="M8.5 6.5 4 11l4.5 4.5" />
    <path d="M12.5 6.5 8 11l4.5 4.5" />
    <path d="M8 11h6a6 6 0 0 1 6 6v1" />
  </Icon>
)

/** A bell with its rings, the reference's mark for the threads that need a person. */
export const IconBellRinging = (p: IconProps) => (
  <Icon {...p}>
    <path d="M7 10.5a5 5 0 0 1 10 0c0 3.2.7 4.6 1.4 5.5H5.6c.7-.9 1.4-2.3 1.4-5.5Z" />
    <path d="M10.2 19a1.9 1.9 0 0 0 3.6 0" />
    <path d="M4.3 7.6a8.4 8.4 0 0 1 2.2-3.3M19.7 7.6a8.4 8.4 0 0 0-2.2-3.3" />
  </Icon>
)

/** A person with a sparkle, the reference's mark for the conversations the AI agent wrote in. */
export const IconUserSparkle = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="10" cy="8" r="3.5" />
    <path d="M3.5 19.5a6.5 6.5 0 0 1 9.4-5.8" />
    <path d="m17.5 13.2.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8Z" />
  </Icon>
)

/** A dollar sign, the reference's mark for a closed deal. */
export const IconDollar = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3.5v17" />
    <path d="M16.5 7.6A3.5 3.5 0 0 0 13.2 5.5h-2.3a3.2 3.2 0 0 0 0 6.4h2.2a3.2 3.2 0 0 1 0 6.4h-2.4a3.5 3.5 0 0 1-3.3-2.1" />
  </Icon>
)

/** A bookmark, the reference's mark for a label somebody made. */
export const IconBookmark = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6.5 5.5A1.5 1.5 0 0 1 8 4h8a1.5 1.5 0 0 1 1.5 1.5v14.2L12 16l-5.5 3.7Z" />
  </Icon>
)

/** A bookmark with a bar across it, the reference's mark for the threads with no label. */
export const IconBookmarkMinus = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6.5 5.5A1.5 1.5 0 0 1 8 4h8a1.5 1.5 0 0 1 1.5 1.5v14.2L12 16l-5.5 3.7Z" />
    <path d="M9.5 10h5" />
  </Icon>
)
